<template>
	<div class="xs-12">
		<!-- Headline -->
		<div class="flex xs-12 xs-between">
			<div class="fs5 lh125 fw7 c1 mb2">{{ $t("notes") }}</div>
			<j-button @click="$modal.show('addNote')" type="tertiary" v-if="canWrite"> <crm-icon name="add" size="small" color="c2" />{{ $t("add") }} </j-button>
		</div>

		<!-- Notes -->
		<div v-for="item in computedList" :key="item.id">
			<j-card class="mb2 bg4--l3" v-if="!item.isDeleting">
				<j-expendable-box>
					<div slot="header" class="p3 fs3 fw7 c1 lh150">
						{{ item.title }}
					</div>
					<div slot="body">
						<div class="pt1 p3">
							<div class="fs3 lh150">
								{{ item.content }}
							</div>
						</div>
						<div class="flex xs-12 bt1 bc7 xs-between xs-middle p3">
							<div class="xs-8 fs3">
								<span class="fw7">{{ item.createdBy }}</span> |
								<span class="c1 fw4">{{ item.createdAt }}</span>
							</div>
							<div class="flex xs-4 xs-right xs-middle" v-if="canWrite">
								<div class="action cu2">
									<crm-icon name="edit" size="normal" color="c6" @click.native="updateNote(item)" />
								</div>
								<div class="action cu2">
									<crm-icon v-if="canWrite" name="delete" size="normal" color="cError" @click.native="deleteNote(item.id)" />
								</div>
							</div>
						</div>
					</div>
				</j-expendable-box>
			</j-card>
		</div>

		<!-- Empty -->
		<j-card class="mb2 bg4--l3 p4 ta2 fs3 c6" v-if="computedList.length === 0">
			{{ $t("errors.no_data") }}
		</j-card>

		<!-- Show more -->
		<div class="xs-12 flex xs-center fs3 c2 fw7 cu2" v-if="numberOfNotes > 5">
			<j-button type="tertiary" v-if="!showMoreNotes" @click="showMoreNotes = true">
				{{ $t("show_all") }}
			</j-button>
			<j-button type="tertiary" v-else @click="showMoreNotes = false">{{ $t("show_less") }}</j-button>
		</div>

		<!-- Add quote -->
		<core-modal name="addNote" :headline="$t('add_note')">
			<create-note :id="id" :notesOf="notesOf" v-on="$listeners" @note:created="$modal.hide('addNote')" />
		</core-modal>

		<!-- Update quote -->
		<core-modal name="updateNote" :headline="$t('update_note')">
			<update-note :id="id" :data="noteToUpdate" :notesOf="notesOf" v-on="$listeners" @note:updated="$modal.hide('updateNote')" />
		</core-modal>
	</div>
</template>

<script>
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JExpendableBox from "@/components/JExpendableBox/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import CreateNote from "./CreateNote.vue"
import UpdateNote from "./UpdateNote.vue"
import dayjs from "dayjs"
import api from "../../api/"
import get from "lodash.get"
import { can } from "@/permissions/"

/**
 * This component is responsible to handle displaying of notes and their updates, creation and deletion
 * It's a smart component that is doing updates, deletes, creation
 * This component doesn't fetch notes though, notes are passed as array via prop list
 * it's because of complexity of our APIs, to get notes, we have to fetch individual, company, policy etc...
 * we currently don't have an API to fetch just notes
 * When this component creates or updates some notes, it triggers events to parent to refetch
 * We can create notes for many different things (individual, company, policys see prop "notesOf")
 */
export default {
	components: {
		JButton,
		JCard,
		JExpendableBox,
		CrmIcon,
		CoreModal,
		CreateNote,
		UpdateNote,
	},

	props: {
		/**
		 * id of client, object, policy and possible others
		 */
		id: {
			type: String,
			required: true,
			default: "",
		},

		/**
		 * Array of notes that we get from GraphQL "note" type
		 */
		list: {
			type: Array,
			required: true,
			default: () => [],
		},

		/**
		 * We can have notes for many things
		 * We have 2 sets of apis entityNote and policyNote
		 * This specifies what to use
		 * It also affects permissions
		 */
		notesOf: {
			validator(val) {
				return val === "policy" || val === "individual" || val === "company" || val === "claim" || val === "case" || typeof val === "string"
			},
			required: true,
			default: "policy",
		},

		canWrite: {
			type: Boolean,
			default: true,
		},
	},

	data() {
		return {
			showMoreNotes: false,

			isDeleteLoading: false,

			/**
			 * Here we save object that will be passed into update-quote as prop
			 */
			noteToUpdate: {
				title: null,
				text: null,
			},

			noteToDeleteId: null,
		}
	},

	computed: {
		computedList() {
			if (!this.list) return []
			const notes = [...this.list].reverse() // show items the most recent the most top

			// Prepare batches for lazy loading
			let output = []
			for (let i = 0; i < notes.length; i++) {
				if (i > 4 && this.showMoreNotes === false) continue
				const item = notes[i]
				output.push({
					id: item.id,
					isDeleting: item.id === this.noteToDeleteId, // this will cause immediate deletion - optimistic UI = no need to wait for refetch
					title: item.title,
					content: item.content,
					createdBy: get(item, "createdBy.associatedUser.name") ? item.createdBy.associatedUser.name : get(item, "createdBy.username"),
					createdAt: item.createdAt ? dayjs(item.createdAt).format(this.$store.state.settings.dateFormat) : null,
				})
			}

			return output
		},

		numberOfNotes() {
			return !this.list ? 0 : this.list.length
		},

		linkToUpdate() {
			return `${this.$route.path}/update-note`
		},
	},

	methods: {
		updateNote(payload) {
			this.noteToUpdate = payload
			this.$modal.show("updateNote")
		},

		async deleteNote(id) {
			// If delete is loading, return
			if (this.isDeleteLoading) return

			// Assign noteToDeleteId so that we can render change of state in list
			this.noteToDeleteId = id

			// Decide if it's adding for policy, entity or claim
			let apiKey = null

			if (this.notesOf === "policy")
				apiKey = {
					query: "deletePolicyNote",
					noteIdKey: "id",
					variable: "policyId",
				}

			if (this.notesOf === "claim")
				apiKey = {
					query: "removeClaimNote",
					noteIdKey: "noteId",
					variable: "claimId",
				}

			if (this.notesOf === "individual" || this.notesOf === "company")
				apiKey = {
					query: "deleteEntityNote",
					noteIdKey: "id",
					variable: "entityId",
				}

			if (this.notesOf === "case")
				apiKey = {
					query: "removeNoteFromCase",
					noteIdKey: "noteId",
					variable: "caseId",
				}

			this.isDeleteLoading = true
			const res = await api[apiKey.query]({
				[apiKey.noteIdKey]: this.noteToDeleteId,
				[apiKey.variable]: this.id,
			})
			this.isDeleteLoading = false

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				this.noteToDeleteId = null // show note if there was an error - optimistic UI
				return
			}

			this.$emit("note:deleted")
		},
	},
}
</script>
